/**
 * Schedule selection rules.
 * Pure functions - no React imports, no side effects.
 */

/** The minimum shape the selection rules need from a schedule list entry. */
export type SelectableSchedule = {
  id: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
};

/**
 * Drops entries the API can legitimately return but the UI cannot offer: nulls from
 * sparse arrays, and records with no id (nothing to select).
 */
export function sanitizeSchedules<T extends SelectableSchedule>(
  schedules: (T | null | undefined)[] | null | undefined
): T[] {
  if (!Array.isArray(schedules)) return [];
  return schedules.filter((schedule): schedule is T => schedule != null && !!schedule.id);
}

function createdAtRank(schedule: SelectableSchedule): number {
  const time = new Date(schedule.createdAt).getTime();
  // Undated or unparseable schedules rank last rather than poisoning the comparison.
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

function byNewestFirst(a: SelectableSchedule, b: SelectableSchedule): number {
  const rankA = createdAtRank(a);
  const rankB = createdAtRank(b);
  if (rankA === rankB) return 0;
  return rankA > rankB ? -1 : 1;
}

/**
 * The schedule a new offering should start on: the coach's explicit default, else their
 * most recently created one.
 *
 * Returns null when the coach owns no schedules. Callers must treat that as a blocking,
 * actionable state — there is no valid offering without a schedule, so silently
 * selecting nothing would only defer the failure to submit time.
 */
export function selectPreferredSchedule<T extends SelectableSchedule>(
  schedules: (T | null | undefined)[] | null | undefined
): T | null {
  const usable = sanitizeSchedules(schedules);
  if (usable.length === 0) return null;

  const explicitDefault = usable.find((schedule) => schedule.isDefault);
  if (explicitDefault) return explicitDefault;

  return [...usable].sort(byNewestFirst)[0];
}

/**
 * Reconciles an already-chosen schedule id against the schedules that actually exist,
 * falling back to the preferred one.
 *
 * A restored draft can outlive the schedule it referenced (renamed is fine, deleted is
 * not), so an id missing from the list must not be trusted just because it was persisted.
 *
 * Only call this once the list is known — reconciling against an empty in-flight list
 * would discard a perfectly valid selection.
 */
export function resolveScheduleSelection<T extends SelectableSchedule>(params: {
  schedules: (T | null | undefined)[] | null | undefined;
  selectedScheduleId?: string | null;
}): string {
  const usable = sanitizeSchedules(params.schedules);
  const selected = params.selectedScheduleId?.trim();

  if (selected && usable.some((schedule) => schedule.id === selected)) {
    return selected;
  }

  return selectPreferredSchedule(usable)?.id ?? "";
}
